import { QueryClient, type Query } from '@tanstack/react-query'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import type { Persister, PersistedClient } from '@tanstack/react-query-persist-client'
import { createLogger } from './logger'
import { getCachedUserId } from './sessionCache'

const log = createLogger('query')

/**
 * Singleton React Query client used by the whole app.
 *
 * Defaults are tuned for a LinkedIn-backed CRM-adjacent product where:
 *   - Data changes slowly enough that aggressive refetching is wasted work
 *   - Users keep the tab open for hours — refetch on window focus is helpful
 *   - Network failures during a LinkedIn blip shouldn't cascade into UI retries
 *     that worsen the ban risk
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is "fresh" for 30s before a refetch is considered. Tweak per
      // query with `staleTime` if you need tighter or looser behaviour.
      staleTime: 30_000,
      // Keep unused query data in memory for 5 min — makes tab-switching snappy.
      gcTime: 5 * 60_000,
      // We want focus-refetch for "did anything change while I was away" UX,
      // but not aggressive background refetching on every window event.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // LinkedIn / Affinity flakes enough that one retry is worth it; more
      // than that risks hammering a service that's already struggling.
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Mutations should fail fast — the caller typically shows a toast and
      // asks the user to retry rather than spinning silently.
      retry: 0,
    },
  },
})

// Surface query/mutation errors through the central logger so future Sentry
// plumbing picks them up automatically. Per-query `onError` handlers still
// run; this is a catch-all safety net.
//
// Dedupe: the cache fires `updated` events for every state transition (fetch
// → pending → error → pending → error → …), so a single failing query would
// otherwise log on every refetch tick. We stash the last-logged error per
// query/mutation and only re-emit when the error instance *changes*. A
// WeakMap lets the GC reclaim the bookkeeping when the query is garbage
// collected by TanStack.
const lastLoggedQueryError = new WeakMap<object, unknown>()
const lastLoggedMutationError = new WeakMap<object, unknown>()

// ─── Persistence ─────────────────────────────────────────────────────────────
//
// Cache is persisted to localStorage so cold page loads paint from cache
// before the first network round-trip. Gives the "instant open" feel a
// real product needs.
//
// Invariants:
//   1. Per-user isolation via the storage key. On sign-out, `clearPersistedQueryCache`
//      wipes the store so the next user doesn't see the previous user's
//      cached rows. See `auth.tsx` sign-out flow.
//   2. Schema-version busting — bump `PERSIST_BUSTER` when a query-key
//      shape changes incompatibly. Old persisted entries are dropped and
//      the app starts fresh rather than returning stale wrong-shape data.
//   3. Don't persist mutation state. Persisted mutations would "replay" on
//      reload, which is catastrophic for side-effectful mutations
//      (e.g. a cached "delete account" mutation re-firing after restart).
//   4. Don't persist queries that opt out via `meta: { persist: false }`.
//      Use for endpoints returning sensitive data (Harmonic, CRM creds)
//      or data that's always fresh anyway.
//
// TTL: `maxAge` below bounds how stale the restored cache can be on load.
// 24h strikes a balance between "snappy cold-start" and "don't show a
// week-old inbox count."

/** Bump to invalidate all persisted caches on next page load. */
const PERSIST_BUSTER = 'v1'

/** Per-user storage key prefix. Falls through to `anon` if no user cached. */
function persistStorageKey(): string {
  const userId = getCachedUserId()
  return `fwdslash:query-cache:${userId ?? 'anon'}`
}

/**
 * Create a storage persister that writes to localStorage under a per-user
 * key. Fresh persister per user avoids cross-user cache bleeds even
 * before an explicit sign-out clear.
 */
export function createQueryPersister(): Persister {
  return createSyncStoragePersister({
    storage: typeof localStorage !== 'undefined' ? localStorage : undefined,
    key: persistStorageKey(),
    // Don't serialize undefined values — they're expensive (become string "undefined")
    // and TanStack's own hydration tolerates missing keys.
    serialize: (client) => JSON.stringify(client),
    deserialize: (raw) => JSON.parse(raw),
  })
}

export const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000 // 24h

/**
 * Query-level `shouldDehydrate` predicate. Controls which queries get
 * persisted:
 *   - Success only — don't persist pending / error states
 *   - Opt-out via `meta.persist: false`
 *   - Skip abort-cancelled queries (they're control-flow, not data)
 */
export function shouldDehydrateQuery(query: Query): boolean {
  if (query.state.status !== 'success') return false
  const persistFlag = (query.meta as { persist?: boolean } | undefined)?.persist
  if (persistFlag === false) return false
  return true
}

/** Version string embedded in each persisted snapshot. Persister drops
 * snapshots whose `buster` doesn't match this value. */
export const QUERY_PERSIST_BUSTER = PERSIST_BUSTER

/**
 * Wipe all persisted query caches for every user on this device. Call from
 * the sign-out flow (or a full site-data nuke) to guarantee no stale data
 * survives an account switch. Matches any key under `fwdslash:query-cache:`.
 */
export function clearAllPersistedQueryCaches(): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('fwdslash:query-cache:')) keysToRemove.push(k)
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k))
  } catch {
    // storage blocked / unavailable — no-op
  }
}

// Hydrate on module load. Separate from React mount so the cache is
// warm before any `useQuery` runs — avoids a flash of loading state.
// Errors during hydration are non-fatal; we just start with an empty cache.
void (async () => {
  try {
    const raw = typeof localStorage !== 'undefined'
      ? localStorage.getItem(persistStorageKey())
      : null
    if (!raw) return
    const parsed = JSON.parse(raw) as PersistedClient | null
    if (!parsed || parsed.buster !== PERSIST_BUSTER) {
      // Version drift — drop the stale cache.
      localStorage.removeItem(persistStorageKey())
      return
    }
    const ageMs = Date.now() - (parsed.timestamp ?? 0)
    if (ageMs > PERSIST_MAX_AGE) {
      localStorage.removeItem(persistStorageKey())
      return
    }
    // Hydrate each query that's still fresh. TanStack's hydrate helper
    // would be ideal but adds a dep chain — manual restore is fine here
    // since we only care about successful query state, not pending.
    for (const dehydrated of parsed.clientState.queries) {
      const { queryKey, queryHash, state } = dehydrated
      if (state.status !== 'success') continue
      queryClient.setQueryData(queryKey, state.data, {
        // setQueryData doesn't accept queryHash; the hash is derived from
        // the key. Passing `updatedAt` preserves freshness math.
        updatedAt: state.dataUpdatedAt,
      })
      void queryHash // unused — TanStack recomputes
    }
  } catch (e) {
    log.warn('failed to hydrate persisted query cache', e)
  }
})()

// Debounced write. `queryCache.subscribe` fires on every state transition,
// which is chatty. Coalesce writes to once per ~1s so we don't hammer
// localStorage with serialisations of a large cache.
let writeTimer: ReturnType<typeof setTimeout> | null = null
const WRITE_DEBOUNCE_MS = 1000
// Sign-out kill switch. `cancelPersist()` flips this false so late
// query invalidations during the sign-out sequence (supabase logout
// firing auth-state-changes, TanStack clearing queries, etc.) can't
// re-schedule a write that would land on disk AFTER we've wiped the
// cache but BEFORE clearSiteData reloads the tab. See POST_FIX_ASTAR_
// AUDIT_HANDOFF finding #6.
let persistingEnabled = true

function schedulePersist(): void {
  if (!persistingEnabled) return
  if (writeTimer !== null) return
  writeTimer = setTimeout(() => {
    writeTimer = null
    persistNow()
  }, WRITE_DEBOUNCE_MS)
}

/**
 * Cancel the pending debounced persist + disable future schedules for
 * the remaining lifetime of this module instance. The module reloads
 * on every sign-out (clearSiteData does `location.replace('/')`), so
 * "remaining lifetime" is typically milliseconds — just long enough
 * for the rest of the sign-out sequence to run without writing
 * user-scoped query data to localStorage.
 *
 * Idempotent. Calling after the flag is already false is a no-op.
 */
export function cancelPersist(): void {
  persistingEnabled = false
  if (writeTimer !== null) {
    clearTimeout(writeTimer)
    writeTimer = null
  }
}

/**
 * TEST-ONLY helper: re-enable the persister. Not exported outside
 * tests — do not call from app code. The sign-out contract assumes
 * `cancelPersist` is terminal for the module instance.
 */
export function _resetPersistingForTests(): void {
  persistingEnabled = true
  if (writeTimer !== null) {
    clearTimeout(writeTimer)
    writeTimer = null
  }
}

function persistNow(): void {
  if (typeof localStorage === 'undefined') return
  try {
    const queries = queryClient.getQueryCache().getAll()
      .filter(shouldDehydrateQuery)
      .map((q) => ({
        queryKey: q.queryKey,
        queryHash: q.queryHash,
        state: q.state,
      }))
    const snapshot: PersistedClient = {
      timestamp: Date.now(),
      buster: PERSIST_BUSTER,
      clientState: { queries, mutations: [] }, // never persist mutations
    }
    localStorage.setItem(persistStorageKey(), JSON.stringify(snapshot))
  } catch (e) {
    // Likely QuotaExceededError if the cache ballooned. Fall back to a
    // full wipe so we don't retain a partial / corrupt snapshot.
    log.warn('persist write failed; clearing cache to recover', e)
    try { localStorage.removeItem(persistStorageKey()) } catch { /* */ }
  }
}

queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== 'updated') return
  const q = event.query
  // Successful / updated queries → schedule a persist write.
  if (q.state.status === 'success') schedulePersist()
  if (q.state.status !== 'error') return
  const err = q.state.error
  if (lastLoggedQueryError.get(q) === err) return
  lastLoggedQueryError.set(q, err)
  log.error('query failed', q.queryKey, err)
})
queryClient.getMutationCache().subscribe((event) => {
  if (event.type !== 'updated') return
  const m = event.mutation
  if (m.state.status !== 'error') return
  const err = m.state.error
  if (lastLoggedMutationError.get(m) === err) return
  lastLoggedMutationError.set(m, err)
  log.error('mutation failed', m.options.mutationKey, err)
})
